import { plain } from '../../lib/labels';
import { useState } from 'react';
import {
  Card,
  Table,
  Tag,
  Typography,
  Space,
  Button,
  Modal,
  Input,
  Select,
  Popconfirm,
  Alert,
  App,
  type TableColumnsType,
} from 'antd';
import {
  KeyOutlined,
  PoweroffOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { Can } from '../../rbac/RequirePermission';

type Gateway =
  | 'razorpay'
  | 'razorpayx'
  | 'google_play'
  | 'agora'
  | 'hive'
  | 'msg91'
  | 'fast2sms'
  | 'fcm'
  | 'ses'
  | 'whatsapp_meta'
  | 'whatsapp_wasender'
  | 'firebase_phone_auth'
  | 'meta_capi';
type Env = 'test' | 'live';

interface RedactedCredential {
  id: string;
  gateway: Gateway;
  env: Env;
  label: string;
  keyId: string;
  valueLast4: string;
  webhookSecretLast4?: string | null;
  config: Record<string, unknown> | null;
  isActive: boolean;
  rotatedAt: string | null;
  lastUsedAt: string | null;
  createdAt: string;
  createdBy: string;
}

const gatewayColor: Record<Gateway, string> = {
  razorpay: 'magenta',
  razorpayx: 'gold',
  google_play: 'green',
  agora: 'cyan',
  hive: 'purple',
  msg91: 'blue',
  fast2sms: 'geekblue',
  fcm: 'green',
  ses: 'default',
  whatsapp_meta: 'green',
  whatsapp_wasender: 'green',
  firebase_phone_auth: 'orange',
  meta_capi: 'blue',
};

// Field hints + helper text per gateway. Drives the create-modal placeholder
// + label copy so the admin knows exactly what to paste.
const GATEWAY_HINTS: Record<Gateway, {
  keyIdLabel: string;
  keyIdPlaceholder: string;
  secretLabel: string;
  secretPlaceholder: string;
  /** Extra non-secret config fields (shape goes into `config` JSON). */
  configFields?: Array<{ key: string; label: string; placeholder: string; required: boolean }>;
  helpUrl?: string;
}> = {
  razorpay: {
    keyIdLabel: 'Key ID',
    keyIdPlaceholder: 'rzp_live_AbC123...',
    secretLabel: 'Key Secret',
    secretPlaceholder: 'Key secret from Razorpay dashboard',
  },
  razorpayx: {
    keyIdLabel: 'Key ID',
    keyIdPlaceholder: 'rzp_live_AbC123...',
    secretLabel: 'Key Secret',
    secretPlaceholder: 'RazorpayX key secret',
  },
  google_play: {
    keyIdLabel: 'Service account client_email',
    keyIdPlaceholder: 'name@project.iam.gserviceaccount.com',
    secretLabel: 'Private key (PEM)',
    secretPlaceholder: '-----BEGIN PRIVATE KEY-----\\n… from the service-account JSON',
    configFields: [
      { key: 'packageName', label: 'App package name', placeholder: 'com.dude.social', required: true },
    ],
  },
  agora: {
    keyIdLabel: 'App ID',
    keyIdPlaceholder: '32-char Agora App ID',
    secretLabel: 'App Certificate',
    secretPlaceholder: 'Agora App Certificate (for token minting)',
  },
  hive: {
    keyIdLabel: 'Project ID',
    keyIdPlaceholder: 'Hive project id',
    secretLabel: 'API Key',
    secretPlaceholder: 'Hive Moderation API key',
  },
  msg91: {
    keyIdLabel: 'Account label',
    keyIdPlaceholder: 'maja-prod',
    secretLabel: 'Auth Key',
    secretPlaceholder: 'MSG91 Auth Key (API → Auth Key, not the panel login)',
    helpUrl: 'https://docs.msg91.com/',
    configFields: [
      {
        key: 'templateId',
        label: 'OTP template ID (MSG91 → OTP → template)',
        placeholder: '64f1c0…',
        required: true,
      },
      {
        key: 'senderId',
        label: 'DLT Sender ID (optional — account default if empty)',
        placeholder: 'MAJAOT',
        required: false,
      },
      {
        key: 'dltTeId',
        label: 'DLT Template Entity ID (optional, some accounts need it)',
        placeholder: '1207…',
        required: false,
      },
    ],
  },
  fast2sms: {
    keyIdLabel: 'Account label',
    keyIdPlaceholder: 'maja-prod',
    secretLabel: 'API Key',
    secretPlaceholder: 'Fast2SMS API key (Dev API → API key)',
    helpUrl: 'https://docs.fast2sms.com/',
    configFields: [
      {
        key: 'route',
        label: 'Route — "otp" (default, Fast2SMS template) or "dlt"',
        placeholder: 'otp',
        required: false,
      },
      {
        key: 'senderId',
        label: 'DLT Sender ID (dlt route only)',
        placeholder: 'MAJAOT',
        required: false,
      },
      {
        key: 'templateId',
        label: 'DLT Message ID of the OTP template (dlt route only)',
        placeholder: '123456',
        required: false,
      },
    ],
  },
  fcm: {
    keyIdLabel: 'Project ID',
    keyIdPlaceholder: 'maja-prod',
    secretLabel: 'Service account JSON',
    secretPlaceholder: 'Paste the full service-account JSON',
  },
  ses: {
    keyIdLabel: 'AWS Access Key ID',
    keyIdPlaceholder: 'AKIA…',
    secretLabel: 'AWS Secret Access Key',
    secretPlaceholder: '40-char secret',
  },
  whatsapp_meta: {
    keyIdLabel: 'Phone Number ID',
    keyIdPlaceholder: '15-digit numeric Meta phone_number_id',
    secretLabel: 'System User Access Token',
    secretPlaceholder: 'EAAGm0... (permanent System User token)',
    helpUrl:
      'https://developers.facebook.com/docs/whatsapp/cloud-api/get-started',
    configFields: [
      {
        key: 'templateName',
        label: 'OTP template name',
        placeholder: 'maja_otp_v1',
        required: true,
      },
      {
        key: 'languageCode',
        label: 'Template language code',
        placeholder: 'en',
        required: true,
      },
      {
        key: 'wabaId',
        label: 'WhatsApp Business Account ID (optional)',
        placeholder: '21-digit numeric WABA id',
        required: false,
      },
      {
        // Verify token for Meta's webhook subscription handshake. Any
        // random string; paste the same value into Meta's Webhooks
        // setup screen. WhatsappWebhookController compares the two and
        // echoes hub.challenge only on match. Optional here because env
        // var WHATSAPP_VERIFY_TOKEN works as a bootstrap fallback.
        key: 'verifyToken',
        label: 'Webhook verify token',
        placeholder: 'Any random string — match Meta’s Webhook setup',
        required: false,
      },
    ],
  },
  whatsapp_wasender: {
    keyIdLabel: 'Label / session id',
    keyIdPlaceholder: 'Free-form — e.g. "maja-prod" or the WAsender session id',
    secretLabel: 'API key (Bearer token)',
    secretPlaceholder: 'WAsender personal access token',
    helpUrl: 'https://wasenderapi.com/api-docs',
    configFields: [
      {
        key: 'baseUrl',
        label: 'Provider API URL',
        placeholder: 'https://www.wasenderapi.com',
        required: true,
      },
      {
        key: 'otpTemplate',
        label: 'OTP message template (must contain {otp})',
        placeholder: '{otp} is your Maja verification code. Do not share it.',
        required: false,
      },
    ],
  },
  firebase_phone_auth: {
    keyIdLabel: 'Project ID',
    keyIdPlaceholder: 'maja-prod (must match service-account project_id)',
    secretLabel: 'Service account JSON',
    secretPlaceholder:
      'Paste the full JSON from Firebase console → Project settings → Service accounts → Generate new private key',
    helpUrl:
      'https://firebase.google.com/docs/admin/setup#initialize_the_sdk_in_non-google_environments',
    configFields: [
      {
        key: 'projectId',
        label: 'Project ID (mirror of keyId, used by the adapter)',
        placeholder: 'maja-prod',
        required: false,
      },
    ],
  },
  meta_capi: {
    keyIdLabel: 'Dataset ID',
    keyIdPlaceholder: 'Numeric dataset (pixel) id from Events Manager',
    secretLabel: 'Conversions API access token',
    secretPlaceholder: 'Events Manager → dataset → Settings → Generate access token',
    helpUrl: 'https://developers.facebook.com/docs/marketing-api/conversions-api/get-started',
    configFields: [
      {
        key: 'testEventCode',
        label: 'Test event code (test env only — never on live)',
        placeholder: 'TEST12345',
        required: false,
      },
      {
        key: 'apiVersion',
        label: 'Graph API version (optional)',
        placeholder: 'v23.0',
        required: false,
      },
    ],
  },
};

export function CredentialsPage() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const [gateway, setGateway] = useState<Gateway | undefined>(undefined);
  const [env, setEnv] = useState<Env | undefined>(undefined);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<{
    gateway: Gateway;
    env: Env;
    label: string;
    keyId: string;
    secret: string;
    webhookSecret: string;
    config: Record<string, string>;
  }>({
    gateway: 'razorpay',
    env: 'test',
    label: '',
    keyId: '',
    secret: '',
    webhookSecret: '',
    config: {},
  });

  const hint = GATEWAY_HINTS[form.gateway];
  // Required config fields must all be filled before submit is enabled.
  const configValid = (hint.configFields ?? [])
    .filter((f) => f.required)
    .every((f) => (form.config[f.key] ?? '').trim().length > 0);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'credentials', gateway, env],
    queryFn: async () =>
      (
        await api.get<RedactedCredential[]>('/admin/credentials', {
          params: { gateway, env },
        })
      ).data,
  });

  const create = useMutation({
    mutationFn: async () => {
      // Strip empty config values so a row never gets persisted with
      // empty-string template names etc.
      const cleanConfig = Object.fromEntries(
        Object.entries(form.config).filter(([, v]) => (v ?? '').trim().length > 0),
      );
      await api.post('/admin/credentials', {
        gateway: form.gateway,
        env: form.env,
        label: form.label,
        keyId: form.keyId,
        secret: form.secret,
        webhookSecret: form.webhookSecret || undefined,
        config: Object.keys(cleanConfig).length > 0 ? cleanConfig : undefined,
      });
    },
    onSuccess: () => {
      message.success('Credential created (inactive). Activate to start using.');
      qc.invalidateQueries({ queryKey: ['admin', 'credentials'] });
      setCreateOpen(false);
      setForm({ ...form, label: '', keyId: '', secret: '', webhookSecret: '', config: {} });
    },
    onError: (e: unknown) => {
      const msg = (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'Could not create credential';
      message.error(msg);
    },
  });

  const activate = useMutation({
    mutationFn: (id: string) => api.post(`/admin/credentials/${id}/activate`),
    onSuccess: () => {
      message.success('Activated — runtime picks up within 60s');
      qc.invalidateQueries({ queryKey: ['admin', 'credentials'] });
    },
    onError: () => message.error('Could not activate'),
  });

  const deactivate = useMutation({
    mutationFn: (id: string) => api.post(`/admin/credentials/${id}/deactivate`),
    onSuccess: () => {
      message.success('Deactivated');
      qc.invalidateQueries({ queryKey: ['admin', 'credentials'] });
    },
    onError: () => message.error('Could not deactivate'),
  });

  const del = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/credentials/${id}`),
    onSuccess: () => {
      message.success('Deleted');
      qc.invalidateQueries({ queryKey: ['admin', 'credentials'] });
    },
    onError: () => message.error('Could not delete'),
  });

  const columns: TableColumnsType<RedactedCredential> = [
    {
      title: 'Paid through',
      dataIndex: 'gateway',
      render: (g: Gateway) => <Tag color={gatewayColor[g]}>{g.toUpperCase()}</Tag>,
    },
    {
      title: 'Test or real',
      dataIndex: 'env',
      render: (e: Env) => <Tag color={e === 'live' ? 'red' : 'default'}>{e === 'live' ? 'Real' : 'Test'}</Tag>,
    },
    {
      title: 'Label',
      dataIndex: 'label',
      render: (l: string, row) => (
        <Space direction="vertical" size={2}>
          <span style={{ fontWeight: 600 }}>{l}</span>
          <span style={{ fontSize: 11, color: '#8B8B8B' }}>
            <code>{row.keyId}</code>
          </span>
        </Space>
      ),
    },
    {
      title: 'Secret key',
      render: (_: unknown, row) => (
        <Space direction="vertical" size={2}>
          <code style={{ color: '#FFD06A' }}>{row.valueLast4}</code>
          {row.webhookSecretLast4 && (
            <span style={{ fontSize: 11, color: '#8B8B8B' }}>
              webhook: <code>{row.webhookSecretLast4}</code>
            </span>
          )}
          {row.config && Object.keys(row.config).length > 0 ? (
            <span style={{ fontSize: 11, color: '#8B8B8B' }}>
              {Object.entries(row.config)
                .map(([k, v]) => `${k}=${String(v)}`)
                .join(' · ')}
            </span>
          ) : null}
        </Space>
      ),
    },
    {
      title: 'Active',
      render: (_: unknown, row) =>
        row.isActive ? (
          <Tag color="success" icon={<CheckCircleOutlined />}>active</Tag>
        ) : (
          <Tag>inactive</Tag>
        ),
    },
    {
      title: 'Last used',
      dataIndex: 'lastUsedAt',
      render: (s: string | null) => (s ? new Date(s).toLocaleString() : '—'),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      render: (s: string) => new Date(s).toLocaleDateString(),
    },
    {
      title: 'Actions',
      render: (_: unknown, row) => (
        <Space>
          <Can permission="credentials.manage">
          {row.isActive ? (
            <Popconfirm
              title="Deactivate this credential?"
              description="Runtime will stop using it within 60s."
              onConfirm={() => deactivate.mutate(row.id)}
              okButtonProps={{ danger: true }}
            >
              <Button size="small" icon={<PoweroffOutlined />}>
                Deactivate
              </Button>
            </Popconfirm>
          ) : (
            <Popconfirm
              title="Activate this credential?"
              description="Any other active credential for this gateway+env will be deactivated."
              onConfirm={() => activate.mutate(row.id)}
            >
              <Button
                size="small"
                type="primary"
                icon={<ReloadOutlined />}
                loading={activate.isPending && activate.variables === row.id}
              >
                Activate
              </Button>
            </Popconfirm>
          )}
          </Can>
          <Can permission="credentials.delete">
            <Popconfirm
              title="Delete credential?"
              description="This cannot be undone. The encrypted secret is destroyed."
              onConfirm={() => del.mutate(row.id)}
              okButtonProps={{ danger: true }}
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Can>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography.Title level={3} style={{ color: 'white', margin: 0 }}>
          Payment & service keys
        </Typography.Title>
        <Space>
          <Select<Gateway | undefined>
            allowClear
            placeholder="All gateways"
            value={gateway}
            onChange={setGateway}
            style={{ minWidth: 180 }}
            options={Object.keys(gatewayColor).map((g) => ({ value: g as Gateway, label: g }))}
          />
          <Select<Env | undefined>
            allowClear
            placeholder="All envs"
            value={env}
            onChange={setEnv}
            style={{ minWidth: 140 }}
            options={[
              { value: 'test' as Env, label: 'test' },
              { value: 'live' as Env, label: 'live' },
            ]}
          />
          <Can permission="credentials.manage">
            <Button type="primary" icon={<KeyOutlined />} onClick={() => setCreateOpen(true)}>
              New
            </Button>
          </Can>
        </Space>
      </div>
      <Alert
        showIcon
        type="info"
        message="Secrets are encrypted at rest with AES-GCM"
        description="The plaintext is never returned by the API after creation. Rotation = create new + activate; old credentials can be deactivated or deleted."
      />
      <Card>
        <Table
          rowKey="id"
          loading={isLoading}
          dataSource={data ?? []}
          columns={columns}
          pagination={{ pageSize: 25 }}
        />
      </Card>

      <Modal
        title="Add gateway credential"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => create.mutate()}
        okText="Create"
        okButtonProps={{
          disabled:
            form.label.length < 2 ||
            form.keyId.length < 2 ||
            form.secret.length < 8 ||
            !configValid,
          loading: create.isPending,
        }}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <div>
            <Typography.Text>Gateway</Typography.Text>
            <Select<Gateway>
              style={{ width: '100%', marginTop: 4 }}
              value={form.gateway}
              onChange={(g) =>
                setForm({ ...form, gateway: g, keyId: '', secret: '', webhookSecret: '', config: {} })
              }
              options={Object.keys(gatewayColor).map((g) => ({ value: g as Gateway, label: g }))}
            />
          </div>
          {form.gateway === 'whatsapp_meta' ? (
            <Alert
              type="warning"
              showIcon
              message="whatsapp_meta is retired"
              description={
                <span style={{ fontSize: 12 }}>
                  The Meta Cloud API adapter has been removed from the
                  runtime. Create a <code>whatsapp_wasender</code> credential
                  instead — that's the active WhatsApp lane now.
                </span>
              }
            />
          ) : null}
          {form.gateway === 'whatsapp_wasender' ? (
            <Alert
              type="info"
              showIcon
              message="WAsender setup"
              description={
                <span style={{ fontSize: 12 }}>
                  Paste the provider's API URL (
                  <code>https://www.wasenderapi.com</code> by default) and the
                  Bearer API key from the WAsender dashboard. The OTP message
                  template can be customised below — must contain the literal{' '}
                  <code>{'{otp}'}</code> placeholder. After saving, flip
                  <code> otp.whatsapp.enabled</code> on the Feature flags page
                  to route OTPs through WhatsApp. Fast2SMS stays as the
                  automatic SMS fallback.
                </span>
              }
            />
          ) : null}
          {form.gateway === 'fast2sms' ? (
            <Alert
              type="info"
              showIcon
              message="Fast2SMS setup"
              description={
                <span style={{ fontSize: 12 }}>
                  Fast2SMS sends every login OTP over SMS (+91 numbers only).
                  Paste the API key from the Fast2SMS dashboard. Leave the
                  route empty to use Fast2SMS's built-in OTP message, or set{' '}
                  <code>dlt</code> plus your DLT Sender ID and the Message ID
                  of an approved OTP template with one{' '}
                  <code>{'{#var#}'}</code> for the code. Activate it right
                  after saving — login OTPs fail until a fast2sms credential
                  is active.
                </span>
              }
            />
          ) : null}
          {form.gateway === 'meta_capi' ? (
            <Alert
              type="info"
              showIcon
              message="Meta Conversions API setup"
              description={
                <span style={{ fontSize: 12 }}>
                  Sends a <code>Purchase</code> event to Meta for each captured
                  Razorpay recharge by a user who opted in to marketing. Use the
                  dataset id as the Dataset ID and a Conversions API token as the
                  secret. On <code>test</code>, add the test event code from
                  Events Manager → Test events; leave it empty on{' '}
                  <code>live</code>, or real purchases only reach the Test
                  events tab. Orders captured while no credential is active are
                  sent once one is activated (up to 6 days back). Deactivate to
                  stop sending.
                </span>
              }
            />
          ) : null}
          {form.gateway === 'msg91' ? (
            <Alert
              type="info"
              showIcon
              message="MSG91 is behind a feature flag"
              description={
                <span style={{ fontSize: 12 }}>
                  SMS OTPs go through <code>fast2sms</code> by default. After
                  activating this credential, turn on
                  <code> otp.sms.msg91.enabled</code> on the Feature flags page
                  to send them through MSG91 instead. Turn the flag off to roll
                  back.
                </span>
              }
            />
          ) : null}
          {form.gateway === 'firebase_phone_auth' ? (
            <Alert
              type="warning"
              showIcon
              message="firebase_phone_auth is retired"
              description={
                <span style={{ fontSize: 12 }}>
                  Login no longer uses Firebase Phone Auth — the API issues
                  every OTP itself and sends it via <code>fast2sms</code>.
                  A credential of this kind has no effect.
                </span>
              }
            />
          ) : null}
          <div>
            <Typography.Text>Environment</Typography.Text>
            <Select<Env>
              style={{ width: '100%', marginTop: 4 }}
              value={form.env}
              onChange={(e) => setForm({ ...form, env: e })}
              options={[
                { value: 'test' as Env, label: 'test' },
                { value: 'live' as Env, label: 'live' },
              ]}
            />
          </div>
          <div>
            <Typography.Text>Label</Typography.Text>
            <Input
              style={{ marginTop: 4 }}
              placeholder="production-2026-may"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
            />
          </div>
          <div>
            <Typography.Text>{hint.keyIdLabel}</Typography.Text>
            <Input
              style={{ marginTop: 4 }}
              placeholder={hint.keyIdPlaceholder}
              value={form.keyId}
              onChange={(e) => setForm({ ...form, keyId: e.target.value })}
            />
          </div>
          <div>
            <Typography.Text>{hint.secretLabel} (encrypted server-side)</Typography.Text>
            <Input.Password
              style={{ marginTop: 4 }}
              placeholder={hint.secretPlaceholder}
              value={form.secret}
              onChange={(e) => setForm({ ...form, secret: e.target.value })}
            />
          </div>
          {/* Gateway-specific config fields. Only render when the picked
              gateway declares them. */}
          {(hint.configFields ?? []).map((f) => (
            <div key={f.key}>
              <Typography.Text>
                {f.label}
                {f.required ? <span style={{ color: '#FB3C7F' }}> *</span> : null}
              </Typography.Text>
              <Input
                style={{ marginTop: 4 }}
                placeholder={f.placeholder}
                value={form.config[f.key] ?? ''}
                onChange={(e) =>
                  setForm({
                    ...form,
                    config: { ...form.config, [f.key]: e.target.value },
                  })
                }
              />
            </div>
          ))}
          {form.gateway !== 'whatsapp_meta' &&
          form.gateway !== 'whatsapp_wasender' &&
          form.gateway !== 'firebase_phone_auth' &&
          form.gateway !== 'meta_capi' &&
          form.gateway !== 'agora' ? (
            <div>
              <Typography.Text>Webhook secret (optional)</Typography.Text>
              <Input.Password
                style={{ marginTop: 4 }}
                placeholder="HMAC signing key"
                value={form.webhookSecret}
                onChange={(e) => setForm({ ...form, webhookSecret: e.target.value })}
              />
            </div>
          ) : null}
          <Alert
            type="warning"
            showIcon
            message="New credentials are inactive by default"
            description="Click Activate after creating to swap the live key. The 60-second cache means traffic rolls over smoothly."
          />
        </Space>
      </Modal>
    </Space>
  );
}
